/**
 * ActiveGameCard Component
 * Card showing an active or waiting game for the spectator view
 */

import React from "react";
import { View, Text, TouchableOpacity, Image } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { ActiveGame } from "../types";

interface ActiveGameCardProps {
  /** Active game data */
  game: ActiveGame;
  /** Whether to show join button for waiting games */
  showJoinButton?: boolean;
  /** Callback when join button pressed */
  onJoin?: () => void;
  /** Callback when spectate button pressed */
  onSpectate?: () => void;
  /** Custom class names */
  className?: string;
}

/**
 * Card displaying an active game session
 */
export const ActiveGameCard: React.FC<ActiveGameCardProps> = ({
  game,
  showJoinButton = true,
  onJoin,
  onSpectate,
  className = "",
}) => {
  const router = useRouter();

  const { session, game: gameDefinition, participants, is_waiting } = game;

  // Format time ago
  const formatTimeAgo = (seconds: number): string => {
    if (seconds < 60) return "nu";
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
    return `${Math.floor(seconds / 3600)}u`;
  };

  const handleSpectate = () => {
    if (onSpectate) {
      onSpectate();
    } else {
      router.push(`/games/${session.game_slug}/spectate/${session.id}`);
    }
  };

  const handleJoin = () => {
    if (onJoin) {
      onJoin();
    } else {
      // Navigate to game to join
      router.push(`/games/${session.game_slug}/play/${session.id}`);
    }
  };

  return (
    <View
      className={`bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 ${className}`}
    >
      {/* Header - Game & Status */}
      <View className="flex-row items-center justify-between mb-3">
        <View className="flex-row items-center flex-1">
          <View
            className="w-10 h-10 rounded-xl items-center justify-center"
            style={{ backgroundColor: gameDefinition.color }}
          >
            <Ionicons
              name={gameDefinition.icon as keyof typeof Ionicons.glyphMap}
              size={22}
              color="#FFFFFF"
            />
          </View>
          <View className="ml-3 flex-1">
            <Text className="font-bold text-gray-900 dark:text-white">
              {gameDefinition.name}
            </Text>
            <View className="flex-row items-center">
              <View
                className={`w-2 h-2 rounded-full mr-1.5 ${
                  is_waiting ? "bg-amber-500" : "bg-green-500"
                }`}
              />
              <Text className="text-sm text-gray-500 dark:text-gray-400">
                {is_waiting ? "Wacht op speler" : "Bezig"} • {formatTimeAgo(game.started_ago_seconds)}
              </Text>
            </View>
          </View>
        </View>

        {/* Live indicator */}
        {!is_waiting && (
          <View className="flex-row items-center bg-red-100 dark:bg-red-900/30 px-2 py-1 rounded-lg">
            <View className="w-2 h-2 bg-red-500 rounded-full mr-1.5 animate-pulse" />
            <Text className="text-xs font-bold text-red-600 dark:text-red-400">
              LIVE
            </Text>
          </View>
        )}
      </View>

      {/* Participants */}
      <View className="flex-row items-center mb-3">
        {participants.map((participant, index) => (
          <React.Fragment key={participant.user_id}>
            <View className="flex-row items-center">
              {participant.avatar_url ? (
                <Image
                  source={{ uri: participant.avatar_url }}
                  className="w-8 h-8 rounded-full border-2 border-white dark:border-gray-800"
                />
              ) : (
                <View className="w-8 h-8 rounded-full bg-purple-200 dark:bg-purple-800 items-center justify-center border-2 border-white dark:border-gray-800">
                  <Text className="text-sm font-bold text-purple-700 dark:text-purple-300">
                    {participant.full_name.charAt(0).toUpperCase()}
                  </Text>
                </View>
              )}
              <Text
                className="ml-2 text-gray-800 dark:text-gray-200 font-medium"
                numberOfLines={1}
              >
                {participant.full_name}
              </Text>
            </View>
            {index < participants.length - 1 && (
              <Text className="mx-2 text-gray-400 font-bold">vs</Text>
            )}
          </React.Fragment>
        ))}

        {/* Show placeholder for waiting games */}
        {is_waiting && participants.length === 1 && (
          <>
            <Text className="mx-2 text-gray-400 font-bold">vs</Text>
            <View className="flex-row items-center">
              <View className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 items-center justify-center border-2 border-dashed border-gray-400">
                <Ionicons name="help" size={16} color="#9CA3AF" />
              </View>
              <Text className="ml-2 text-gray-400 italic">Wacht...</Text>
            </View>
          </>
        )}
      </View>

      {/* Mode indicator */}
      <View className="flex-row items-center mb-3">
        <View
          className={`px-2 py-1 rounded-lg ${
            session.mode === "weekend"
              ? "bg-amber-100 dark:bg-amber-900/30"
              : "bg-gray-100 dark:bg-gray-700"
          }`}
        >
          <Text
            className={`text-xs font-medium ${
              session.mode === "weekend"
                ? "text-amber-700 dark:text-amber-300"
                : "text-gray-600 dark:text-gray-400"
            }`}
          >
            {session.mode === "weekend" ? "🏆 Weekend Competitie" : "🎮 Vrij Spel"}
          </Text>
        </View>
      </View>

      {/* Actions */}
      <View className="flex-row">
        {is_waiting && showJoinButton ? (
          <TouchableOpacity
            onPress={handleJoin}
            className="flex-1 flex-row items-center justify-center bg-purple-600 py-3 rounded-xl"
            accessibilityLabel="Doe mee aan spel"
            accessibilityRole="button"
          >
            <Ionicons name="enter-outline" size={20} color="#FFFFFF" />
            <Text className="ml-2 font-bold text-white">Doe mee</Text>
          </TouchableOpacity>
        ) : (
          <TouchableOpacity
            onPress={handleSpectate}
            className="flex-1 flex-row items-center justify-center bg-gray-100 dark:bg-gray-700 py-3 rounded-xl"
            accessibilityLabel="Bekijk spel"
            accessibilityRole="button"
          >
            <Ionicons name="eye-outline" size={20} color="#6B7280" />
            <Text className="ml-2 font-medium text-gray-700 dark:text-gray-300">
              Bekijken
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

export default ActiveGameCard;

